import { zodResolver } from "@hookform/resolvers/zod"
import { Dialog, DialogActions, DialogContent } from "@mui/material"
import type { FC } from "react"
import { useState } from "react"
import { useForm } from "react-hook-form"

import { DEFAULT_DELIVERY_PACKAGING } from "../../constants/deliveryPackaging"
import { useSaveDeliveryPackaging } from "../../hooks/useSaveDeliveryPackaging"
import { deliveryPackagingSchema } from "../../schemas/deliveryPackaging"
import type { DeliveryPackagingFormValues } from "../../types"

import { DeliveryPackagingForm } from "./DeliveryPackagingForm"

import { Button } from "@/components/common/Button"
import { CancelDialog } from "@/components/common/Modals"
import DialogHeader from "@/components/common/Modals/DialogHeader"
import type { LocationDeliveryPackagingPreference } from "@/graphql/codegen/graphql"

interface EditDeliveryPackagingDialogProps {
  onClose: (shouldRefetch?: boolean) => void
  locationId: string
  initialData?: LocationDeliveryPackagingPreference | null
}

export const EditDeliveryPackagingDialog: FC<EditDeliveryPackagingDialogProps> = ({
  onClose,
  locationId,
  initialData,
}) => {
  const [showCancelDialog, setShowCancelDialog] = useState(false)
  const { saveDeliveryPackaging, isSaving } = useSaveDeliveryPackaging()

  const isEditMode = !!initialData
  const dialogTitle = isEditMode ? "Edit Delivery & Packaging Data" : "Add Delivery & Packaging Data"

  const defaultValues: DeliveryPackagingFormValues = initialData
    ? {
        preferredDeliveryDateReference: initialData.preferredDeliveryDateReference ?? null,
        packagingInstructions: initialData.packagingInstructions ?? "",
        insureSmallParcel: initialData.insureSmallParcel ?? false,
      }
    : DEFAULT_DELIVERY_PACKAGING

  const {
    handleSubmit,
    formState: { isDirty, isSubmitting, isValid },
    control,
  } = useForm<DeliveryPackagingFormValues>({
    defaultValues,
    resolver: zodResolver(deliveryPackagingSchema),
    mode: "onChange",
  })

  const handleClose = () => {
    if (isDirty) {
      setShowCancelDialog(true)
    } else {
      onClose()
    }
  }

  const handleCancelDialogClose = () => {
    setShowCancelDialog(false)
  }

  const handleConfirmClose = () => {
    setShowCancelDialog(false)
    onClose()
  }

  const onSubmit = async (data: DeliveryPackagingFormValues) => {
    const success = await saveDeliveryPackaging({
      locationId,
      formValues: data,
    })
    if (success) {
      onClose(true)
    }
  }

  return (
    <>
      <Dialog open onClose={handleClose} fullWidth maxWidth='sm' data-testid='delivery-packaging-dialog'>
        <DialogHeader title={dialogTitle} onClose={handleClose} />

        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogContent className='px-10 pb-1 pt-8'>
            <DeliveryPackagingForm control={control} />
          </DialogContent>

          <DialogActions className='justify-between p-8'>
            <Button
              appearance='outlined'
              onClick={handleClose}
              disabled={isSubmitting}
              data-testid='delivery-packaging-cancel-button'
            >
              Cancel
            </Button>
            <Button
              type='submit'
              disabled={!isValid || !isDirty || isSubmitting || isSaving}
              data-testid='delivery-packaging-save-button'
            >
              {isSaving ? "Saving..." : "Save Changes"}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {showCancelDialog && <CancelDialog onClose={handleCancelDialogClose} onConfirm={handleConfirmClose} />}
    </>
  )
}
